// OPT-DEC-01B — Human decision register, separate from price application and observed outcome.
import React, { useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, Divider, FormControlLabel,
  Paper, Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPricingOptimizationDecisions,
  recordPricingOptimizationDecision,
} from '../services/pricingDecisionService';
import type {
  PricingOptimizationDecisionChoice,
  PricingOptimizationDecisionCommand,
  PricingOptimizationDecisionEvent,
} from '../types/pricingDecision';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';

const labels: Record<PricingOptimizationDecisionChoice, string> = {
  pending: 'Dejar pendiente de revisión',
  accept_reference: 'Aceptar la recomendación como referencia',
  alternative_price: 'Decidir un precio alternativo',
  discarded: 'Descartar la recomendación',
};
const numberText = (value: number | null): string => value === null || !Number.isFinite(value)
  ? 'No disponible'
  : pricingNumberFormat({ maximumFractionDigits: 6 }).format(value);
const readableDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Fecha no disponible' : date.toLocaleString('es-ES');
};
const errorText = (error: unknown): string => {
  const code = (error as { response?: { data?: { detail?: { code?: string } } } })
    ?.response?.data?.detail?.code;
  const messages: Record<string, string> = {
    DECISION_REVISION_CONFLICT: 'Otra persona registró una decisión. Actualiza la ficha antes de continuar.',
    DECISION_COMMAND_CONFLICT: 'La solicitud ya se utilizó con otros datos. Revisa la decisión y vuelve a enviarla.',
    DECISION_NO_RECOMMENDATION: 'Esta evaluación no dispone de una recomendación que pueda aceptarse.',
    DECISION_LEGACY_NOT_ELIGIBLE: 'Los registros históricos parciales no permiten registrar decisiones en esta versión.',
    DECISION_EVALUATION_NOT_FOUND: 'No se encontró una evaluación archivada accesible para esta cuenta.',
    DECISION_SCHEMA_NOT_READY: 'El registro de decisiones todavía no está habilitado en este entorno.',
    DECISION_READ_UNAVAILABLE: 'No se pudieron consultar las decisiones. Inténtalo de nuevo.',
    DECISION_WRITE_UNCONFIRMED: 'No se pudo confirmar el guardado. Consulta el estado antes de reintentarlo.',
  };
  return messages[code ?? ''] ?? 'No se pudo completar la operación. Consulta el estado antes de reintentarlo.';
};

function HumanDecisionRow({ event }: { event: PricingOptimizationDecisionEvent }) {
  return <Box sx={{ py: 1.5, borderBottom: '1px solid rgba(148,163,184,0.15)' }}>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent="space-between">
      <Typography color="#e2f2fd" fontWeight={800}>{labels[event.choice]}</Typography>
      <Typography variant="caption" color="#91aec2">{readableDate(event.recorded_at)}</Typography>
    </Stack>
    {event.decided_price !== null && <Typography variant="body2" color="#bfe3ed" sx={{ mt: 0.35 }}>
      {event.price_source === 'archived_recommendation' ? 'Precio tomado como referencia' : 'Precio alternativo decidido'}: {numberText(event.decided_price)}
      {' · '}Moneda no registrada
    </Typography>}
    {event.reason && <Typography variant="body2" color="#aabfd0" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{event.reason}</Typography>}
  </Box>;
}

interface Props {
  optimizationId: string;
  hasRecommendation: boolean;
}

const OptimizationDecisionSection: React.FC<Props> = ({ optimizationId, hasRecommendation }) => {
  const queryClient = useQueryClient();
  const queryKey = ['pricingOptimizationDecisions', optimizationId];
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey,
    queryFn: () => getPricingOptimizationDecisions(optimizationId),
    retry: 1,
  });
  const [choice, setChoice] = useState<PricingOptimizationDecisionChoice | ''>('');
  const [reason, setReason] = useState('');
  const [alternative, setAlternative] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const pendingCommandRef = useRef<{ signature: string; command: PricingOptimizationDecisionCommand } | null>(null);
  const mutation = useMutation({
    mutationFn: (command: PricingOptimizationDecisionCommand) =>
      recordPricingOptimizationDecision(optimizationId, command),
    onSuccess: () => {
      pendingCommandRef.current = null;
      setChoice('');
      setReason('');
      setAlternative('');
      setLocalError(null);
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (failure) => {
      setLocalError(errorText(failure));
      const code = (failure as { response?: { data?: { detail?: { code?: string } } } })
        ?.response?.data?.detail?.code;
      if (code === 'DECISION_REVISION_CONFLICT') {
        pendingCommandRef.current = null;
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  const cleanedReason = reason.trim();
  const cleanPrice = alternative.trim().replace(',', '.');
  const parsedPrice = Number(cleanPrice);
  const validPrice = /^\d+(?:\.\d{1,6})?$/.test(cleanPrice)
    && Number.isFinite(parsedPrice) && parsedPrice > 0 && parsedPrice < 100000000000000;
  const needsReason = choice === 'alternative_price' || choice === 'discarded';
  const canSend = Boolean(data && choice && !mutation.isPending
    && (!needsReason || (cleanedReason.length > 0 && cleanedReason.length <= 1000))
    && (!needsReason || choice !== 'alternative_price' || validPrice)
    && (hasRecommendation || (choice !== 'accept_reference' && choice !== 'alternative_price')));

  const save = () => {
    if (!data || !choice || !canSend) return;
    const inputPrice = choice === 'alternative_price' ? cleanPrice : null;
    const signature = JSON.stringify({ choice, cleanedReason, inputPrice, revision: data.revision });
    const retained = pendingCommandRef.current;
    const command: PricingOptimizationDecisionCommand = retained?.signature === signature
      ? retained.command
      : {
          version: '1.0',
          command_id: crypto.randomUUID(),
          expected_revision: data.revision,
          choice,
          reason: cleanedReason || null,
          alternative_price: inputPrice,
        };
    pendingCommandRef.current = { signature, command };
    setLocalError(null);
    mutation.mutate(command);
  };

  return <Paper sx={{ bgcolor: '#111d2e', color: '#e2e8f0', border: '1px solid rgba(86,210,195,0.28)', borderRadius: 2, p: { xs: 2, md: 2.3 }, backgroundImage: 'none' }}>
    <Stack direction={{ xs: 'column', md: 'row' }} gap={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
      <Box>
        <Typography variant="overline" sx={{ color: '#75e4d4', fontWeight: 850, letterSpacing: 1 }}>PASO 2 · DECISIÓN COMERCIAL</Typography>
        <Typography variant="h6" sx={{ fontWeight: 850, color: '#f8fafc' }}>¿Qué vamos a hacer con este precio?</Typography>
        <Typography variant="body2" sx={{ color: '#adc6d7', mt: 0.5 }}>Elige qué hacer; el precio no se cambia automáticamente.</Typography>
      </Box>
      {data && <Chip label={data.current ? `Revisión ${data.revision}` : 'Sin decisión registrada'} size="small"
        sx={{ bgcolor: '#193d43', color: '#b8fff1', fontWeight: 800 }} />}
    </Stack>

    {isLoading && <Stack direction="row" gap={1.5} sx={{ mt: 3 }} alignItems="center"><CircularProgress size={22} /><Typography>Cargando decisiones…</Typography></Stack>}
    {isError && <Alert severity="error" sx={{ mt: 2 }} action={<Button color="inherit" onClick={() => void refetch()}>Reintentar</Button>}>{errorText(error)}</Alert>}

    {data && <>
      <Box sx={{ bgcolor: '#122d36', border: '1px solid #2e5d64', borderRadius: 1.5, px: 1.7, py: 1, mt: 1.2 }}>
        <Typography variant="caption" color="#7acfc4" fontWeight={800}>DECISIÓN VIGENTE</Typography>
        {data.current ? <HumanDecisionRow event={data.current} /> :
          <Typography variant="body2" color="#b4cbd8" sx={{ mt: 1 }}>Todavía no se ha registrado una decisión humana.</Typography>}
      </Box>
      <Divider sx={{ borderColor: '#314658', my: 1.5 }} />
      <Typography variant="subtitle1" fontWeight={850} color="#f2f9ff">Selecciona una opción</Typography>
      <RadioGroup value={choice} onChange={(event) => {
        setChoice(event.target.value as PricingOptimizationDecisionChoice);
        setLocalError(null);
      }} sx={{ mt: 0.7, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' }, gap: 0.3 }}>
        {(Object.keys(labels) as PricingOptimizationDecisionChoice[]).map((value) => <FormControlLabel key={value}
          value={value} control={<Radio size="small" />} label={labels[value]}
          disabled={mutation.isPending || (!hasRecommendation && (value === 'accept_reference' || value === 'alternative_price'))}
          sx={{ m: 0, py: 0.15, '& .MuiFormControlLabel-label': { fontSize: 14, color: '#d3e7f4' } }} />)}
      </RadioGroup>
      {choice === 'alternative_price' && <TextField label="Precio alternativo por unidad" size="small" fullWidth
        value={alternative} onChange={(event) => setAlternative(event.target.value)}
        placeholder="Ej.: 24,50" sx={{ maxWidth: 370, mt: 1.6 }}
        helperText="Importe positivo con hasta seis decimales. La moneda no consta en este archivo." />}
      {choice && <TextField label={needsReason ? 'Motivo de la decisión (obligatorio)' : 'Motivo (opcional)'}
        value={reason} onChange={(event) => setReason(event.target.value)}
        size="small" multiline minRows={2} fullWidth inputProps={{ maxLength: 1000 }}
        sx={{ mt: 1.8 }} helperText={`${reason.length}/1000 caracteres`} />}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} sx={{ mt: 2 }}>
        <Button variant="contained" disabled={!canSend} onClick={save}
          sx={{ bgcolor: '#77e7d4', color: '#082e34', fontWeight: 850, '&:hover': { bgcolor: '#b4faee' } }}>
          {mutation.isPending ? 'Guardando…' : 'Guardar decisión'}
        </Button>
        <Typography variant="caption" color="#8caabd">Una revisión nueva conserva intactas las anteriores.</Typography>
      </Stack>
      {localError && <Alert severity="error" sx={{ mt: 1.5 }} action={<Button color="inherit" onClick={() => void refetch()}>Actualizar</Button>}>{localError}</Alert>}
      <Typography variant="caption" color="#9bc2d3" sx={{ display: 'block', mt: 1.4 }}>Guardar una decisión no confirma que el precio se haya aplicado. El seguimiento real se registrará por separado.</Typography>
      <Accordion disableGutters sx={{ mt: 1.3, bgcolor: '#0e1a2b', color: '#e2e8f0', border: '1px solid #314658', boxShadow: 'none', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: '#9bc5d6' }} />}>
          <Typography variant="body2" fontWeight={800}>Historial de decisiones ({data.history.length})</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {data.history.length === 0 ? <Typography variant="body2" color="#9ab3c6">No hay decisiones registradas.</Typography>
            : [...data.history].reverse().map((event) => <HumanDecisionRow key={event.id} event={event} />)}
          {data.history_limited && <Typography variant="caption" color="#c6d4e1">Se muestran las 200 decisiones más recientes; el archivo completo se conserva.</Typography>}
        </AccordionDetails>
      </Accordion>
    </>}
  </Paper>;
};

export default OptimizationDecisionSection;
